import { computed, type Ref, ref } from 'vue';

export interface ProgressInfo {
    fileIndex: number;
    loaded: number;
    total: number;
}

export interface SignedUrlResponse {
    uuid: string;
    key: string;
    bucket: string;
    url: string;
}

export interface StoredFileInfo {
    uuid: string;
    key: string;
    bucket: string;
    name: string;
    size: number;
    content_type: string;
}

export interface VaporFileUploaderOptions {
    signUrl: string;
    bucket: string;
    expires?: string;
    visibility?: string;
    scope: 'tenant' | 'central' | 'crew';
    uploadUrl?: string;
}

export interface UploadProgress {
    loaded: number;
    total: number;
    percent: number;
}

export function useVaporFileUploader(options: VaporFileUploaderOptions) {
    const axios = computed(() => {
        if (options.scope === 'tenant') {
            return window.tenantAxios;
        } else if (options.scope === 'crew') {
            return window.crewAxios;
        } else {
            return window.centralAxios;
        }
    });

    const progress: Ref<Map<number, UploadProgress>> = ref(new Map());

    async function sign(files: File[]): Promise<SignedUrlResponse[]> {
        const promises: Promise<SignedUrlResponse>[] = [];
        const { signUrl, bucket, expires, visibility } = options;

        files.forEach((file: File) => {
            const promise = (async () => {
                const resp = await axios.value.post(signUrl, {
                    bucket,
                    content_type: file.type,
                    expires: expires || '',
                    visibility: visibility || '',
                });
                return resp.data;
            })();
            promises.push(promise);
        });

        const results = await Promise.allSettled(promises);

        const rejected = results.find((result) => result.status === 'rejected');
        if (rejected) {
            throw new Error('Signing :: Signing failure');
        }

        return results.map((result) => (result as PromiseFulfilledResult<SignedUrlResponse>).value);
    }

    async function store(
        files: File[],
        signedUrls: SignedUrlResponse[],
        onProgress?: (info: ProgressInfo) => void,
    ): Promise<StoredFileInfo[]> {
        const promises: Promise<StoredFileInfo>[] = [];

        signedUrls.forEach((signedUrlResponse, idx) => {
            const promise = (async () => {
                const BASE_API_URL = import.meta.env.VITE_API_BASE_URL;
                const axiosInstance = axios.value.create({
                    baseURL: BASE_API_URL,
                    headers: {
                        'X-Request-With': 'XMLHttpRequest',
                        'Content-Type': files[idx].type,
                    },
                });

                await axiosInstance.put(signedUrlResponse.url, files[idx], {
                    onUploadProgress: (progressEvent) => {
                        const loaded = progressEvent.loaded;
                        const total = progressEvent.total || files[idx].size;
                        const percent = total > 0 ? Math.round((loaded / total) * 100) : 0;

                        progress.value.set(idx, { loaded, total, percent });

                        if (onProgress) {
                            onProgress({ fileIndex: idx, loaded, total });
                        }
                    },
                });

                return {
                    uuid: signedUrlResponse.uuid,
                    key: signedUrlResponse.key,
                    bucket: signedUrlResponse.bucket,
                    name: files[idx].name,
                    size: files[idx].size,
                    content_type: files[idx].type,
                };
            })();

            promises.push(promise);
        });

        const results = await Promise.allSettled(promises);

        const rejected = results.find((result) => result.status === 'rejected');
        if (rejected) {
            throw new Error('Storing :: Upload failure');
        }

        return results
            .filter((result) => result.status === 'fulfilled')
            .map((result) => (result as PromiseFulfilledResult<StoredFileInfo>).value);
    }

    async function upload(
        storedFiles: StoredFileInfo[],
        additionalPayload: Record<string, unknown> = {},
    ): Promise<unknown[]> {
        if (!options.uploadUrl) {
            throw new Error('Upload URL is required for upload function');
        }

        const promises: Promise<unknown>[] = [];

        storedFiles.forEach((storedFile) => {
            const promise = axios.value.post(options.uploadUrl, {
                uuid: storedFile.uuid,
                key: storedFile.key,
                bucket: storedFile.bucket,
                name: storedFile.name,
                content_type: storedFile.content_type,
                size: Math.ceil(storedFile.size / 1024),
                ...additionalPayload,
            });
            promises.push(promise);
        });

        const results = await Promise.allSettled(promises);

        const rejected = results.find((result) => result.status === 'rejected');
        if (rejected) {
            const rejectedResult = rejected as PromiseRejectedResult;
            throw rejectedResult.reason;
        }

        return results
            .filter((result) => result.status === 'fulfilled')
            .map((result) => (result as PromiseFulfilledResult<unknown>).value);
    }

    function resetProgress() {
        progress.value.clear();
    }

    return {
        sign,
        store,
        upload,
        progress,
        resetProgress,
    };
}
